import type { HistoryEventRecord, OperatingSchedule } from '../../domain/types.js';
import { chatComplete, deepSeekReady } from '../../llm/client.js';
import { storyFromModelText } from './catchUp.js';
import { formatWorldClockPrompt, inWorldDateFromIso } from './clock.js';

export { storyFromModelText };

/** World-local calendar date from the live clock only (not persisted state). */
export function resolveCurrentInWorldDate(
  clockIso: string,
  schedule: OperatingSchedule,
): string {
  return inWorldDateFromIso(clockIso, schedule);
}

/** Latest world-local date that has at least one history event. */
export function latestWorldDateWithEvents(
  events: HistoryEventRecord[],
  schedule: OperatingSchedule,
): string | null {
  let latest: string | null = null;
  for (const event of events) {
    const date = inWorldDateFromIso(event.occurredAt, schedule);
    if (!latest || date > latest) latest = date;
  }
  return latest;
}

/** Date whose events are narrated: today when it has notes, else the latest dated day. */
export function resolveNarratedWorldDate(
  clockIso: string,
  schedule: OperatingSchedule,
  events: HistoryEventRecord[],
): string {
  const today = resolveCurrentInWorldDate(clockIso, schedule);
  if (eventsOnWorldDate(events, today, schedule).length > 0) return today;
  return latestWorldDateWithEvents(events, schedule) ?? today;
}

export function eventsOnWorldDate(
  events: HistoryEventRecord[],
  currentInWorldDate: string,
  schedule: OperatingSchedule,
): HistoryEventRecord[] {
  return events.filter(
    (event) => inWorldDateFromIso(event.occurredAt, schedule) === currentInWorldDate,
  );
}

export function eventsForCharacterOnWorldDate(
  events: HistoryEventRecord[],
  characterId: string,
  currentInWorldDate: string,
  schedule: OperatingSchedule,
): HistoryEventRecord[] {
  return eventsOnWorldDate(events, currentInWorldDate, schedule)
    .filter((event) => event.actorIds.includes(characterId));
}

export interface DayStoryArgs {
  events: HistoryEventRecord[];
  roster: Array<{ id: string; firstName: string }>;
  schedule?: OperatingSchedule;
  worldClockIso?: string;
  locationRegion?: string;
  /** World-local calendar date being narrated (may differ from the clock when today has no events). */
  narratedWorldDate?: string;
}

export async function generateDayStory(args: DayStoryArgs): Promise<string | null> {
  if (!deepSeekReady() || args.events.length === 0) return null;
  const idToName = new Map(args.roster.map((npc) => [npc.id, npc.firstName]));
  const notes = args.events.map((event) => ({
    occurredAt: event.occurredAt,
    summary: event.summary,
    actors: event.actorIds.flatMap((id) => {
      const name = idToName.get(id);
      return name ? [name] : [];
    }),
  }));
  const response = await chatComplete([
    {
      role: 'system',
      content: [
        'Write a narrative of this day\'s events listed below.',
        'Include every character who appears in the notes.',
        'Be compelling and accurate.',
        'Do not invent people, places, or events that are not in the notes.',
        'Do not mention line counts, character counts, "compiled from", or "ready to observe".',
        'Do not dump a timestamp log.',
        'Prefer one or two paragraphs. Never more than four.',
        'Return JSON only: {"paragraphs":["paragraph one","paragraph two"]}.',
      ].join(' '),
    },
    {
      role: 'user',
      content: JSON.stringify({
        ...(args.narratedWorldDate ? { narratedWorldDate: args.narratedWorldDate } : {}),
        ...(args.worldClockIso && args.schedule
          ? {
              worldClock: formatWorldClockPrompt(
                args.worldClockIso,
                args.schedule,
                args.locationRegion,
              ),
              worldClockNote: 'This clock is the world\'s time.',
            }
          : {}),
        roster: args.roster.map((npc) => npc.firstName),
        notes,
      }),
    },
  ], { json: true, maxTokens: 800, temperature: 0.7 });
  return storyFromModelText(response);
}

export interface FetchHistoryPanelStoryArgs {
  events: HistoryEventRecord[];
  schedule: OperatingSchedule;
  worldClockIso: string;
  locationRegion?: string;
  roster: Array<{ id: string; firstName: string }>;
}

/** Gathers the narrated day's events and awaits DeepSeek when needed. Safe before opening a panel. */
export async function fetchHistoryPanelStory(args: FetchHistoryPanelStoryArgs): Promise<string | null> {
  if (args.events.length === 0) return null;

  const narratedWorldDate = resolveNarratedWorldDate(
    args.worldClockIso,
    args.schedule,
    args.events,
  );
  const dayEvents = eventsOnWorldDate(args.events, narratedWorldDate, args.schedule);
  if (dayEvents.length === 0 || !deepSeekReady()) return null;

  try {
    return await generateDayStory({
      events: dayEvents,
      roster: args.roster,
      schedule: args.schedule,
      worldClockIso: args.worldClockIso,
      locationRegion: args.locationRegion,
      narratedWorldDate,
    });
  } catch {
    return null;
  }
}
